/*
  Ridge & River accessibility fixes (Oct 2026).
  Loaded from layout/theme.liquid. Covers what Liquid alone can't:
  1. Announces the result of a variant change (in stock / sold out / unavailable).
  2. Gallery thumbnails: mirrors the theme's is-nav-selected class into aria-current.
  3. Rebuy cart drawer: keeps the header cart link's aria-expanded in sync and returns focus on close.
  4. Klaviyo pop-up: Escape closes it, it gets a real accessible name and heading, focus returns on close.
  5. App-drawn form fields get autocomplete hints.
*/
(function () {
  'use strict';

  /* One polite live region that lives outside anything the theme re-renders. */
  var live = null;
  function announce(text) {
    if (!text) return;
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 120);
  }

  /* 1. Variant change. The theme fires variant:changed on the product form once its re-render lands. */
  document.addEventListener('variant:changed', function (e) {
    var d = e.detail || {};
    var v = d.variant;
    if (!v || !v.id) {
      announce('This combination is unavailable');
      return;
    }
    announce((v.public_title || v.title) + ': ' + (v.available ? 'in stock' : 'sold out'));
  });

  /* 2. Gallery thumbnails. */
  function syncThumb(a) {
    if (a.classList.contains('is-nav-selected')) a.setAttribute('aria-current', 'true');
    else a.removeAttribute('aria-current');
  }

  /* 3. Rebuy cart drawer. Rebuy toggles .is-visible on its .rebuy-cart dialog. */
  var cartTrigger = null;
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[aria-controls="mini-cart"], [data-action="toggle-mini-cart"]');
    if (t) cartTrigger = t;
  }, true);

  function syncCart(cart) {
    var open = cart.classList.contains('is-visible');
    var links = document.querySelectorAll('a[aria-controls="mini-cart"], [data-action="toggle-mini-cart"]');
    for (var i = 0; i < links.length; i++) links[i].setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) return;
    setTimeout(function () {
      var a = document.activeElement;
      if ((!a || a === document.body || cart.contains(a)) && cartTrigger && document.contains(cartTrigger)) {
        cartTrigger.focus();
      }
    }, 60);
  }

  /* 4. Klaviyo pop-up. */
  var lastFocus = null;
  var klaviyoReturnTo = null;
  var klaviyoOpen = null;

  document.addEventListener('focusin', function (e) {
    if (!e.target.closest('.klaviyo-form, [role="dialog"]')) lastFocus = e.target;
  });

  function isShown(el) {
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function findKlaviyoDialog() {
    var list = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
    for (var i = 0; i < list.length; i++) {
      if (list[i].querySelector('.klaviyo-form') && isShown(list[i])) return list[i];
    }
    return null;
  }

  function nameKlaviyoDialog(dlg) {
    if (dlg.getAttribute('data-rr-named')) return;
    var first = dlg.querySelector('.klaviyo-form-richtext');
    /* The rich-text block holds the headline AND the body copy. Only its first non-empty line is the headline. */
    var headline = null;
    if (first) {
      var lines = first.querySelectorAll('p');
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].textContent.trim()) { headline = lines[i]; break; }
      }
      if (!headline) headline = first;
    }
    var text = headline ? headline.textContent.trim().split(/\s+/).join(' ') : '';
    dlg.setAttribute('aria-label', text ? 'Email sign-up: ' + text.slice(0, 80) : 'Email sign-up');
    if (headline && !dlg.querySelector('h1, h2, h3, [role="heading"]')) {
      headline.setAttribute('role', 'heading');
      headline.setAttribute('aria-level', '2');
    }
    dlg.setAttribute('data-rr-named', '1');
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var dlg = findKlaviyoDialog();
    if (!dlg) return;
    var close = dlg.querySelector('.klaviyo-close-form');
    if (close) {
      e.stopPropagation();
      close.click();
      setTimeout(queueScan, 300);
    }
  }, true);

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.klaviyo-form, [role="dialog"]')) setTimeout(queueScan, 300);
  }, true);

  function scanKlaviyo() {
    var dlg = findKlaviyoDialog();
    if (dlg && dlg !== klaviyoOpen) {
      klaviyoOpen = dlg;
      klaviyoReturnTo = lastFocus;
      nameKlaviyoDialog(dlg);
    } else if (!dlg && klaviyoOpen) {
      klaviyoOpen = null;
      var a = document.activeElement;
      if ((!a || a === document.body) && klaviyoReturnTo && document.contains(klaviyoReturnTo)) {
        klaviyoReturnTo.focus();
      }
    }
  }

  /* 5. Autocomplete hints (WCAG 1.3.5) for forms drawn by apps (Klaviyo, Qikify, contact form app).
     The theme's own forms carry the attribute in their markup already. */
  var AUTOCOMPLETE = [
    ['first name', 'given-name'], ['last name', 'family-name'], ['full name', 'name'], ['your name', 'name'],
    ['email', 'email'], ['phone', 'tel'], ['company', 'organization'], ['zip', 'postal-code'], ['postal', 'postal-code']
  ];
  /* The field's own name/id, with [ ] - _ turned into spaces: "fields[first_name]" -> " fields first name ". */
  function fieldKey(el) {
    return (' ' + [el.name, el.id].join(' ') + ' ').toLowerCase().split(/[\[\]\-_]+/).join(' ');
  }
  /* What the person sees: label, placeholder, aria-label. Used only to rule fields out and for the two name halves. */
  function fieldLabel(el) {
    var bits = [el.placeholder, el.getAttribute('aria-label')];
    if (el.labels) for (var i = 0; i < el.labels.length; i++) bits.push(el.labels[i].textContent);
    return bits.join(' ').toLowerCase();
  }
  function addAutocomplete() {
    /* "on" says nothing about the field's purpose (some apps stamp it on everything), so it is replaced too. */
    var fields = document.querySelectorAll('input:not([autocomplete]):not([type="hidden"]), input[autocomplete="on"]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      var type = (el.getAttribute('type') || 'text').toLowerCase();
      if (type !== 'text' && type !== 'email' && type !== 'tel') continue;
      if (el.closest('form[action*="/search"], [role="search"], .product-form')) continue;
      var key = fieldKey(el);
      var label = fieldLabel(el);
      var both = key + ' ' + label;
      if (both.indexOf('recipient') !== -1 || both.indexOf('gift') !== -1) continue;
      var value = type === 'email' ? 'email' : (type === 'tel' ? 'tel' : '');
      /* Purpose comes from the field's name/id, not from label prose ("Email me when it's back" is not an email field). */
      for (var j = 0; !value && j < AUTOCOMPLETE.length; j++) {
        if (key.indexOf(' ' + AUTOCOMPLETE[j][0] + ' ') !== -1) value = AUTOCOMPLETE[j][1];
      }
      if (!value && label.indexOf('first name') !== -1) value = 'given-name';
      if (!value && label.indexOf('last name') !== -1) value = 'family-name';
      if (!value && key.indexOf(' name ') !== -1 && key.indexOf('team') === -1 && key.indexOf('business') === -1 && key.indexOf('logo') === -1) value = 'name';
      if (value) el.setAttribute('autocomplete', value);
    }
  }

  /* One observer for all of the above. */
  var scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    setTimeout(function () {
      scanQueued = false;
      var thumbs = document.querySelectorAll('.product-gallery__thumbnail');
      for (var i = 0; i < thumbs.length; i++) syncThumb(thumbs[i]);
      scanKlaviyo();
      addAutocomplete();
    }, 80);
  }

  function onMutations(records) {
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      if (r.type === 'attributes') {
        var t = r.target;
        if (t.classList.contains('product-gallery__thumbnail')) syncThumb(t);
        else if (t.classList.contains('rebuy-cart')) syncCart(t);
        else if (t.closest && t.closest('.klaviyo-form, [role="dialog"]')) queueScan();
      } else if (r.addedNodes.length || r.removedNodes.length) {
        queueScan();
      }
    }
  }

  function start() {
    new MutationObserver(onMutations).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
    queueScan();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
